import Loader from "components/Loader/loader"
import moment from "moment"
import React, { useEffect, useState } from "react"
import { FaPlus, FaEdit } from "react-icons/fa"
import { MdCancel, MdDelete, MdEdit } from "react-icons/md"
import Select from "react-select"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  Table,
  Row,
  Col,
  FormFeedback,
  Label,
  Card,
  CardBody,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"

const customSelectStyles = {
  control: (styles, { isFocused }) => ({
    ...styles,
    borderRadius: "20px",
    padding: "2px 4px",
    borderColor: isFocused ? "#007BFF" : "#ced4da",
    boxShadow: isFocused ? "0 0 0 0.2rem rgba(0, 123, 255, 0.25)" : "none",
    "&:hover": { borderColor: "#007BFF" },
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? "#007BFF" : isFocused ? "#F1F1F1" : "white",
    color: isSelected ? "white" : "#333",
    padding: "10px 8px",
    cursor: "pointer",
    borderRadius: "20px",
  }),
  menu: styles => ({
    ...styles,
    backgroundColor: "#fff",
  }),
  singleValue: styles => ({ ...styles, color: "#333" }),
}

const formatTo12Hour = time => {
  if (!time) return ""
  const [hour, minute] = time.split(":")
  let h = parseInt(hour)
  const ampm = h >= 12 ? "PM" : "AM"
  h = h % 12 || 12
  return `${h}:${minute} ${ampm}`
}

const calculateSlotHours = (start, end) => {
  if (!start || !end) return 0
  const [sh, sm] = start.split(":").map(Number)
  const [eh, em] = end.split(":").map(Number)
  let diff = (eh * 60 + em - (sh * 60 + sm)) / 60
  if (diff < 0) diff += 24
  return diff
}

const formatHours = hours => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  return `${h}h ${m}m`
}

const calculateTotalTime = (inTime, outTime) => {
  if (!inTime || !outTime) return "0h 0m"
  const [inH, inM] = inTime.split(":").map(Number)
  const [outH, outM] = outTime.split(":").map(Number)

  let totalMinutes = outH * 60 + outM - (inH * 60 + inM)
  if (totalMinutes < 0) totalMinutes += 24 * 60 // handle overnight (unlikely here)

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return `${hours}h ${minutes}m`
}

const LocationTimeModal = ({
  isOpen,
  toggle,
  selectedLocation,
  locationName,
  fetchLocations,
  startDate,
  endDate = "",
  // employmentType,
}) => {
  const [week, setweek] = useState("")
  const [day, setday] = useState("")
  const [inTime, setInTime] = useState("09:00")
  const [outTime, setOutTime] = useState("19:00")
  const [slots, setSlots] = useState([])
  const [uniqueEmployees, setUniqueEmployees] = useState([])
  const [editId, setEditId] = useState(null)
  const [employees, setEmployees] = useState([])
  const [allAvailableEmployees, setAllAvailableEmployees] = useState([])
  const [copyWeekEnabled, setCopyWeekEnabled] = useState(false)
  const [copyToWeek, setCopyToWeek] = useState("")
  // const [selectedSourceSlot, setSelectedSourceSlot] = useState("")

  const [errors, setErrors] = useState({
    week: "",
    day: "",
    inTime: "",
    outTime: "",
    employees: "",
    copyToWeek: "",
  })

  const [loading, setloading] = useState(false)

  const fieldsDisabled = copyWeekEnabled // || !!selectedSourceSlot
  const dayDisabled = copyWeekEnabled

  useEffect(() => {
    if (selectedLocation) {
      getLocationSlots()
      setweek("")
      setday("")
      setInTime("09:00")
      setOutTime("19:00")
      setEmployees([])
      setUniqueEmployees([])
      setAllAvailableEmployees([])
      setEditId(null)
      setErrors({ day: "", inTime: "", outTime: "", employees: "" })
    }
  }, [selectedLocation])

  async function getLocationSlots() {
    setloading(true)
    const APIdata = await fetchData(setloading, `slot/${selectedLocation}`)
    if (APIdata?.success) {
      setSlots(APIdata?.data || [])
    } else {
      setSlots([])
    }
    setloading(false)
  }

  useEffect(() => {
    if (!isOpen) return
    if (day !== "" && inTime && outTime) {
      getLocationEmployees()
    } else {
      setUniqueEmployees([])
    }
  }, [day, inTime, outTime, isOpen])

  async function getLocationEmployees() {
    const APIdata = await FetchPostData(
      setloading,
      `slot/available/employees`,
      {
        week,
        day: Number(day),
        inTime,
        outTime,
        locationId: selectedLocation,
      }
    )
    if (APIdata?.data?.success) {
      setUniqueEmployees(APIdata?.data?.employees || [])
      setAllAvailableEmployees(APIdata?.data?.employees || [])
    }
  }

  const validateInputs = () => {
    const newErrors = {}

    if (copyWeekEnabled) {
      if (!copyToWeek || copyToWeek === "") {
        newErrors.copyToWeek = "Please select a target week"
      }
      setErrors(newErrors)
      return Object.keys(newErrors).length === 0
    }

    if (day === "" || day === null || day === undefined) {
      newErrors.day = "Please select a day"
    }

    if (!inTime) {
      newErrors.inTime = "Please select in time"
    }

    if (!outTime) {
      newErrors.outTime = "Please select out time"
    } else if (inTime && outTime <= inTime) {
      newErrors.outTime = "Out time must be after in time"
    }

    if (!employees || employees.length === 0) {
      newErrors.employees = "Please select at least one employee"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAddOrUpdateSlot = async () => {
    if (!validateInputs()) {
      return
    }

    try {
      if (copyWeekEnabled && copyToWeek) {
        const allWeeks = generateWeeks(startDate, endDate)
        const destWeek = allWeeks.find(w => w.label === week)

        const copyPayload = {
          sourceWeek: copyToWeek,
          destinationWeek: week,
          locationId: selectedLocation,
          destinationWeekStart: destWeek?.startDate || "",
          destinationWeekEnd: destWeek?.endDate || "",
        }

        const result = await FetchPostData(setloading, "slot/copy-week", copyPayload)

        if (result?.data?.success) {
          const { successCount, skipCount, errorCount, errors: copyErrors } = result.data
          let msg = `${successCount} slot(s) copied to current week.`
          if (skipCount > 0) msg += ` ${skipCount} skipped (already exist).`
          if (errorCount > 0) msg += ` ${errorCount} failed: ${copyErrors?.join("; ")}`

          Swal.fire({
            title: "Week Copied!",
            text: msg,
            icon: successCount > 0 ? "success" : "warning",
            timer: 3000,
          })

          const allSlots = (result?.data?.allSlots || []).map(s => ({
            ...s,
            week: getWeekLabel(s.week),
          }))
          setSlots(allSlots)
          resetForm()
          fetchLocations?.()
        } else {
          throw new Error(result?.data?.message || "Failed to copy week")
        }
      } else {
        const allWeeks = generateWeeks(startDate, endDate)
        const selectedWeek = allWeeks.find(w => w.label === week)

        const payload = {
          week,
          weekStart: selectedWeek?.startDate || "",
          weekEnd: selectedWeek?.endDate || "",
          day: Number(day),
          inTime,
          outTime,
          employees: JSON.stringify(employees.map(e => e.value)),
        }

        let APIData
        if (editId) {
          APIData = await FetchPutData(setloading, "slot", editId, payload)
        } else {
          APIData = await FetchPostData(setloading, "slot", {
            ...payload,
            locationId: selectedLocation,
          })
        }

        if (APIData?.data?.success) {
          Swal.fire({
            title: editId ? "Slot Updated!" : "Slot Added!",
            text: "Location time slot saved successfully.",
            icon: "success",
            timer: 2000,
          })
          if (APIData?.data?.allSlots) {
            const allSlots = APIData.data.allSlots.map(s =>
              s.id === APIData?.data?.slot?.id
                ? { ...s, week: week }
                : { ...s, week: getWeekLabel(s.week) }
            )
            setSlots(allSlots)
          } else {
            getLocationSlots()
          }
          resetForm()
          fetchLocations?.()
        } else {
          throw new Error(APIData?.response?.data?.message || "Unknown error")
        }
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "Failed to save slot. Please try again.",
        icon: "error",
      })
    }
  }

  const resetForm = () => {
    setweek("")
    setday("")
    setInTime("09:00")
    setOutTime("19:00")
    setEmployees([])
    setUniqueEmployees([])
    setAllAvailableEmployees([])
    setEditId(null)
    setCopyWeekEnabled(false)
    setCopyToWeek("")
    // setSelectedSourceSlot("")
    setErrors({
      day: "",
      inTime: "",
      outTime: "",
      employees: "",
      copyToWeek: "",
    })
  }

  const handleRemoveSlot = async id => {
    const result = await Swal.fire({
      title: "Delete Slot?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchDeleteData(setloading, "slot", id)
      if (APIdata?.data?.success) {
        Swal.fire("Deleted!", "Slot removed successfully.", "success")
        if (APIdata?.data?.allSlots) {
          setSlots(
            APIdata.data.allSlots.map(s => ({
              ...s,
              week: getWeekLabel(s.week),
            }))
          )
        } else {
          getLocationSlots()
        }
        fetchLocations?.()
      }
    }
  }

  const handleEditSlot = slot => {
    setweek(slot.week || "")
    setday(slot.day)
    setInTime(slot.inTime)
    setOutTime(slot.outTime)
    setEditId(slot.id)
    setEmployees(
      slot?.employees?.map(emp => ({
        value: emp.id,
        label: emp.name,
      })) || []
    )
    // setSelectedSourceSlot("")
    setCopyWeekEnabled(false)
    setCopyToWeek("")
    setErrors({}) // Clear errors on edit
  }

  const handleCancelEdit = () => {
    resetForm()
  }

  const generateWeeks = (start, end) => {
    if (!start || !end) return []

    const weeks = []
    let current = moment(start)
    const last = moment(end)

    let weekNum = 1
    while (current.isBefore(last) || current.isSame(last, "day")) {
      const weekEnd = moment(current).add(6, "days")
      weeks.push({
        label: `Week ${weekNum} (${current.format(
          "DD/MM/YYYY"
        )}-${weekEnd.format("DD/MM/YYYY")})`,
        value: weekNum,
        startDate: current.format("YYYY-MM-DD"),
        endDate: weekEnd.isAfter(last)
          ? last.format("YYYY-MM-DD")
          : weekEnd.format("YYYY-MM-DD"),
      })
      current = weekEnd.add(1, "day")
      weekNum++
    }
    return weeks
  }

  const getWeekLabel = weekValue => {
    const allWeeks = generateWeeks(startDate, endDate)
    const numVal = Number(weekValue)
    const match = allWeeks.find(w => w.value === numVal)
    return match ? match.label : weekValue
  }
  const days = [
    { label: "Monday", value: 1 },
    { label: "Tuesday", value: 2 },
    { label: "Wednesday", value: 3 },
    { label: "Thursday", value: 4 },
    { label: "Friday", value: 5 },
    { label: "Saturday", value: 6 },
    { label: "Sunday", value: 0 },
  ]

  return (
    <Modal isOpen={isOpen} toggle={toggle} fullscreen>
      {loading && <Loader />}
      <ModalHeader toggle={toggle}>
        Set Location Time{" "}
        <span className="fw-bold ms-3">
          ({locationName}) (
          {endDate ? moment(startDate).format("DD/MM/YYYY") : ""}{" "}
          {endDate ? `- ${moment(endDate).format("DD/MM/YYYY")}` : "Continuous"}
          )
        </span>
      </ModalHeader>
      <ModalBody>
        <Card className="mb-0 shadow-sm">
          <CardBody className="p-4">
            <h5 className="mb-4 text-primary">
              {editId ? "Edit Time Slot" : "Add New Time Slot"}
            </h5>

            <Row className="align-items-end g-3">
              {/* {employmentType === "contractual" && (
                <Col md="2">
                  <Label className="fw-medium mb-1">
                    Week <span className="text-danger">*</span>
                  </Label>
                  <select
                    value={week}
                    onChange={e => {
                      setweek(e.target.value)
                      if (errors.week) setErrors({ ...errors, week: "" })
                    }}
                    className={`form-select rounded-4 ${
                      errors.week ? "is-invalid" : ""
                    }`}
                    style={{ height: "38px" }}
                  >
                    <option value="">Select Week</option>
                    {generateWeeks(startDate, endDate).map(w => (
                      <option key={w.value} value={w.label}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                  {errors.week && <FormFeedback>{errors.week}</FormFeedback>}
                </Col>
              )} */}
              {/* {employmentType === "contractual" && (
                <Col md="1" className="text-center">
                  <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "13px" }}>Copy Week</Label>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-4 border"
                    style={{
                      height: "38px",
                      backgroundColor: copyWeekEnabled ? "#e7f1ff" : "#fff",
                      borderColor: copyWeekEnabled ? "#0d6efd" : "#ced4da",
                      cursor: editId ? "not-allowed" : "pointer",
                    }}
                    onClick={() => {
                      if (editId) return
                      const next = !copyWeekEnabled
                      setCopyWeekEnabled(next)
                      if (!next) setCopyToWeek("")
                      if (errors.copyToWeek)
                        setErrors({ ...errors, copyToWeek: "" })
                    }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      id="copyWeekCheck"
                      checked={copyWeekEnabled}
                      disabled={!!editId || !!selectedSourceSlot}
                      onChange={e => {
                        setCopyWeekEnabled(e.target.checked)
                        if (e.target.checked) setSelectedSourceSlot("")
                        if (!e.target.checked) setCopyToWeek("")
                        if (errors.copyToWeek)
                          setErrors({ ...errors, copyToWeek: "" })
                      }}
                      style={{
                        width: 18,
                        height: 18,
                        cursor: editId ? "not-allowed" : "pointer",
                      }}
                    />
                  </div>
                </Col>
              )} */}
              {/* {employmentType === "contractual" && copyWeekEnabled && (
                <Col md="2">
                  <Label className="fw-medium mb-1">
                    Target Week <span className="text-danger">*</span>
                  </Label>
                  <select
                    value={copyToWeek}
                    onChange={e => {
                      setCopyToWeek(e.target.value)
                      if (errors.copyToWeek)
                        setErrors({ ...errors, copyToWeek: "" })
                    }}
                    className={`form-select rounded-4 ${
                      errors.copyToWeek ? "is-invalid" : ""
                    }`}
                    style={{ height: "38px" }}
                  >
                    <option value="">Select Target Week</option>
                    {generateWeeks(startDate, endDate)
                      .filter(w => w.label !== week && slots.some(s => s.week === w.label))
                      .map(w => (
                        <option key={w.value} value={w.label}>
                          {w.label}
                        </option>
                      ))}
                  </select>
                  {errors.copyToWeek && (
                    <FormFeedback>{errors.copyToWeek}</FormFeedback>
                  )}
                </Col>
              )} */}
              <Col md="2">
                <Label className="fw-medium mb-1">
                  Day <span className="text-danger">*</span>
                </Label>
                <select
                  value={day}
                  disabled={dayDisabled}
                  onChange={e => {
                    setday(e.target.value)
                    if (errors.day) setErrors({ ...errors, day: "" })
                  }}
                  className={`form-select rounded-4 ${
                    errors.day ? "is-invalid" : ""
                  }`}
                  style={{ height: "38px" }}
                >
                  <option value="">Select day</option>
                  {days.map(d => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
                {errors.day && <FormFeedback>{errors.day}</FormFeedback>}
              </Col>

              {/* Use Existing Slot - commented out
              <Col md="2">
                <Label className="fw-medium mb-1">Use Existing Slot</Label>
                <select
                  value={selectedSourceSlot}
                  disabled={!!editId || copyWeekEnabled}
                  onChange={e => {
                    const slotId = e.target.value
                    setSelectedSourceSlot(slotId)
                    if (slotId) {
                      setCopyWeekEnabled(false)
                      setCopyToWeek("")
                      const found = slots.find(s => String(s.id) === String(slotId))
                      if (found) {
                        setday(found.day)
                        setInTime(found.inTime)
                        setOutTime(found.outTime)
                        setEmployees(
                          found.employees?.map(emp => ({
                            value: emp.id,
                            label: emp.name,
                          })) || []
                        )
                      }
                    } else {
                      setday("")
                      setInTime("09:00")
                      setOutTime("19:00")
                      setEmployees([])
                    }
                  }}
                  className="form-select rounded-4"
                  style={{ height: "38px" }}
                >
                  <option value="">None</option>
                  {slots.map(s => {
                    const dayLabel = days.find(d => d.value === s.day)?.label || ""
                    const empNames = s.employees?.map(e => e.name).join(", ") || "No employees"
                    return (
                      <option key={s.id} value={s.id}>
                        {getWeekLabel(s.week)} - {dayLabel} ({formatTo12Hour(s.inTime)} - {formatTo12Hour(s.outTime)}) - {empNames}
                      </option>
                    )
                  })}
                </select>
              </Col>
              */}

              <Col md="2">
                <Label className="fw-medium mb-1">
                  In Time <span className="text-danger">*</span>
                </Label>
                <Input
                  type="time"
                  value={inTime}
                  disabled={fieldsDisabled}
                  invalid={!!errors.inTime}
                  style={{ height: "38px" }}
                  onChange={e => {
                    setInTime(e.target.value)
                    setEmployees([])
                    if (errors.inTime) setErrors({ ...errors, inTime: "" })
                  }}
                />
                {errors.inTime && <FormFeedback>{errors.inTime}</FormFeedback>}
              </Col>

              <Col md="2">
                <Label className="fw-medium mb-1">
                  Out Time <span className="text-danger">*</span>
                </Label>
                <Input
                  type="time"
                  value={outTime}
                  disabled={fieldsDisabled}
                  invalid={!!errors.outTime}
                  style={{ height: "38px" }}
                  onChange={e => {
                    setOutTime(e.target.value)
                    setEmployees([])
                    if (errors.outTime) setErrors({ ...errors, outTime: "" })
                  }}
                />
                {errors.outTime && (
                  <FormFeedback>{errors.outTime}</FormFeedback>
                )}
              </Col>

              <Col md="2">
                <Label className="fw-medium mb-1">
                  Employees <span className="text-danger">*</span>
                </Label>
                  <Select
                    isMulti
                    styles={{
                      ...customSelectStyles,
                      control: (base, state) => ({
                        ...base,
                        minHeight: "38px",
                        borderRadius: "1rem",
                        borderColor: state.isFocused ? "#007BFF" : "#ced4da",
                      }),
                      valueContainer: base => ({
                        ...base,
                        flexWrap: "wrap",
                        gap: "2px",
                        padding: "2px 8px",
                        maxHeight: "76px",
                        overflowY: "auto",
                      }),
                    }}
                  value={employees}
                  isDisabled={fieldsDisabled}
                  onChange={option => {
                    setEmployees(option || [])
                    if (errors.employees)
                      setErrors({ ...errors, employees: "" })
                  }}
                  options={uniqueEmployees.map(emp => ({
                    value: emp?.id,
                    label: emp?.name,
                  }))}
                  placeholder="Select employees..."
                  className={errors.employees ? "is-invalid" : ""}
                />
                {errors.employees && (
                  <small className="text-danger d-block mt-1">
                    {errors.employees}
                  </small>
                )}
              </Col>

              <Col md="1">
                <Label className="fw-medium mb-1">&nbsp;</Label>
                <div className="d-flex gap-2">
                  <Button
                    color={editId ? "warning" : "success"}
                    onClick={handleAddOrUpdateSlot}
                    disabled={loading}
                    style={{ height: "38px", minWidth: "100px" }}
                  >
                    {editId ? <MdEdit size={16} /> : <FaPlus size={16} />}
                    <span className="ms-1">{editId ? "Update" : "Add"}</span>
                  </Button>
                  {editId && (
                    <Button color="secondary" onClick={handleCancelEdit}>
                      <MdCancel size={18} />
                    </Button>
                  )}
                </div>
              </Col>
            </Row>

            <hr className="my-4" />

            <h5 className="mb-3 text-primary">Existing Time Slots</h5>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              <Table bordered hover responsive size="sm">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    {/* {employmentType === "contractual" && <th>Week</th>} */}
                    <th>Day</th>
                    <th>In Time</th>
                    <th>Out Time</th>
                    <th>Duration</th>
                    <th>Employees</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.length > 0 ? (
                    slots.map((slot, index) => (
                      <tr key={slot.id}>
                        <td>{index + 1}</td>
                        {/* {employmentType === "contractual" && (
                          <td>{getWeekLabel(slot.week)}</td>
                        )} */}
                        <td>{days.find(d => d.value === slot.day)?.label}</td>
                        <td>{formatTo12Hour(slot.inTime)}</td>
                        <td>{formatTo12Hour(slot.outTime)}</td>
                        <td>{calculateTotalTime(slot.inTime, slot.outTime)}</td>
                        <td>
                          {slot?.employees?.map(e => e.name).join(", ") || "-"}
                        </td>
                        <td>
                          <Button
                            color="info"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEditSlot(slot)}
                          >
                            <FaEdit size={14} />
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleRemoveSlot(slot.id)}
                          >
                            <MdDelete size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-3">
                        No time slots added yet for this location.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </ModalBody>
    </Modal>
  )
}

export default LocationTimeModal
